"use client";

// Hand-drawn or vector parcel-map texture, drawn in code as SVG lines in the current text color.
// Cards use <ParcelTexture settings={...} />; the settings are tuned on the local /styles page.
import { useEffect, useId, useMemo, useState } from "react";

type Pt = [number, number];
type Line = { pts: Pt[]; weight: number };

function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Smooth 1D noise from a few random sine waves, roughly in [-1, 1].
function wave(rand: () => number, layers = 3) {
  const parts = Array.from({ length: layers }, (_, i) => ({ f: (i + 1) * (0.6 + rand() * 0.9), p: rand() * Math.PI * 2, a: 1 / (i + 1) }));
  const norm = parts.reduce((n, w) => n + w.a, 0);
  return (t: number) => parts.reduce((v, w) => v + Math.sin(t * w.f * Math.PI * 2 + w.p) * w.a, 0) / norm;
}

// Smooth 2D noise, roughly in [-1, 1]: used to make districts of tight, normal and big lots.
function field(rand: () => number) {
  const parts = Array.from({ length: 4 }, (_, i) => {
    const a = rand() * Math.PI;
    const f = (0.7 + rand() * 0.8) * (i + 1);
    return { ax: Math.cos(a) * f, ay: Math.sin(a) * f, p: rand() * Math.PI * 2, w: 1 / (i + 1) };
  });
  const norm = parts.reduce((n, q) => n + q.w, 0);
  return (x: number, y: number) => parts.reduce((v, q) => v + Math.sin((x * q.ax + y * q.ay) / 520 * Math.PI * 2 + q.p) * q.w, 0) / norm * 1.6;
}

const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// Break a long run of points wherever keep() fails, then into pencil-length strokes that overlap a little.
// With split off (vector mode), each run stays one clean line.
function toStrokes(pts: Pt[], keep: (p: Pt) => boolean, rand: () => number, weight: number, split = true): Line[] {
  const runs: Pt[][] = [];
  let cur: Pt[] = [];
  for (const p of pts) {
    if (keep(p)) cur.push(p);
    else if (cur.length) { runs.push(cur); cur = []; }
  }
  if (cur.length) runs.push(cur);

  const out: Line[] = [];
  for (const run of runs) {
    if (run.length < 2) continue;
    if (!split) { out.push({ pts: run, weight }); continue; }
    let start = 0;
    while (start < run.length - 1) {
      let len = 0;
      const target = 150 + rand() * 260;
      let end = start;
      while (end < run.length - 1 && len < target) { len += dist(run[end], run[end + 1]); end++; }
      if (run.length - 1 - end < 6) end = run.length - 1;
      out.push({ pts: run.slice(start, end + 1), weight });
      start = Math.max(start + 1, end - 1); // tiny overlap where the pencil picks up again
    }
  }
  return out;
}

// A recipe describes one kind of neighborhood; the seed picks a particular one.
export type Recipe = {
  name: string;
  note: string;
  spacing: number; // average distance between streets
  spacingVar: number; // 0 = even blocks, 1 = very uneven
  curve: number; // shared bend across all streets
  wander: number; // how much each street bends on its own
  bow: number; // crescent: streets arc around the middle
  cross: { lean: number; amp: number }[]; // roughly vertical streets
  culDeSacs: number;
  frontage: [number, number];
  bigLots: number; // share of extra-wide parcels
  tilt: number; // how crooked lot lines are
  rotate: number;
};

export const recipes: Recipe[] = [
  { name: 'Gentle suburb', note: 'The current draft: soft curves, one cross street, one cul-de-sac.', spacing: 200, spacingVar: 0.35, curve: 55, wander: 22, bow: 0, cross: [{ lean: 0.18, amp: 70 }], culDeSacs: 1, frontage: [26, 46], bigLots: 0.1, tilt: 0.28, rotate: -7 },
  { name: 'Crescents', note: 'Streets arc around a center, like Palmer Woods.', spacing: 175, spacingVar: 0.2, curve: 20, wander: 12, bow: 160, cross: [{ lean: -0.35, amp: 40 }, { lean: 0.4, amp: 40 }], culDeSacs: 0, frontage: [24, 40], bigLots: 0.06, tilt: 0.2, rotate: -4 },
  { name: 'Cul-de-sac cluster', note: 'Deep blocks with several dead-ends.', spacing: 270, spacingVar: 0.15, curve: 40, wander: 18, bow: 0, cross: [], culDeSacs: 3, frontage: [28, 48], bigLots: 0.08, tilt: 0.3, rotate: -10 },
  { name: 'Old town grid', note: 'Nearly straight streets and narrow rowhouse lots.', spacing: 135, spacingVar: 0.1, curve: 8, wander: 4, bow: 0, cross: [{ lean: 0.05, amp: 6 }, { lean: 0.05, amp: 6 }, { lean: 0.05, amp: 6 }], culDeSacs: 0, frontage: [14, 24], bigLots: 0.05, tilt: 0.1, rotate: -12 },
  { name: 'Big lots', note: 'Wide parcels and few lines. The calmest option.', spacing: 260, spacingVar: 0.3, curve: 70, wander: 25, bow: 0, cross: [{ lean: 0.25, amp: 90 }], culDeSacs: 1, frontage: [60, 120], bigLots: 0.2, tilt: 0.35, rotate: -6 },
  { name: 'Winding hills', note: 'Streets that bend a lot, each in its own way.', spacing: 215, spacingVar: 0.4, curve: 90, wander: 45, bow: 0, cross: [{ lean: 0.3, amp: 110 }], culDeSacs: 1, frontage: [26, 46], bigLots: 0.12, tilt: 0.32, rotate: -3 },
  { name: 'Diagonal avenue', note: 'Gentle streets cut by one strong diagonal.', spacing: 185, spacingVar: 0.2, curve: 18, wander: 10, bow: 0, cross: [{ lean: 1.1, amp: 20 }], culDeSacs: 1, frontage: [22, 38], bigLots: 0.08, tilt: 0.18, rotate: -8 },
  { name: 'Mixed edges', note: 'Uneven blocks: some tight, some deep, two dead-ends.', spacing: 205, spacingVar: 0.7, curve: 45, wander: 28, bow: 40, cross: [{ lean: 0.15, amp: 60 }], culDeSacs: 2, frontage: [20, 50], bigLots: 0.14, tilt: 0.3, rotate: -9 },
];

type Cds = { x: number; street: number; C: Pt; poly: Pt[] };

function inPoly(poly: Pt[], p: Pt) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// Vector mode only. A circuit-trace route: follows a smooth curve at widely spaced knots,
// joined by flat runs and 45° jogs, so a street is all straight lines with sharp bends.
type Route = { at: (t: number) => number; verts: Pt[] };
type JogStats = { before: number; after: number };
// fixed pseudo-random number per knot, so vector routes never touch the hand-drawn tab's random stream
const knotHash = (k: number, salt: number) => {
  const s = Math.sin(k * 12.9898 + salt * 0.0137) * 43758.5453;
  return s - Math.floor(s);
};
function octilinear(f: (t: number) => number, from: number, to: number, step = 200, snap = 10, stats?: JogStats, dropShare = 0.35): Route {
  const lim = step * 0.7; // a jog can never be longer than its run, so the route stays continuous
  const knots: Pt[] = [];
  for (let t = from - step; t <= to + step * 2; t += step) {
    const want = Math.round(f(t) / snap) * snap;
    const prev = knots.length ? knots[knots.length - 1][1] : want;
    knots.push([t, Math.max(prev - lim, Math.min(prev + lim, want))]);
  }

  // Remove about a third of the 45° jogs: each removed jog's rise is folded into the next jog
  // (or the previous one at the end of the route), so the street stays continuous with fewer diagonals.
  const deltas = knots.slice(1).map((k, i) => k[1] - knots[i][1]);
  const jogs = deltas.map((d, i) => (d !== 0 ? i : -1)).filter(i => i >= 0);
  const target = Math.round(jogs.length * dropShare);
  const most = Math.max(target, Math.floor(jogs.length * 0.4));
  let removed = 0;
  for (const k of [...jogs].sort((a, b) => knotHash(a, from + 7) - knotHash(b, from + 7))) {
    if (removed >= target) break;
    if (deltas[k] === 0) continue;
    let m = deltas.findIndex((d, q) => q > k && d !== 0);
    if (m < 0) for (let q = k - 1; q >= 0; q--) if (deltas[q] !== 0) { m = q; break; }
    if (m < 0) continue;
    const merged = deltas[m] + deltas[k];
    const gone = merged === 0 ? 2 : 1; // opposite jogs cancel out entirely
    if (Math.abs(merged) > lim || removed + gone > most) continue;
    deltas[m] = merged;
    deltas[k] = 0;
    removed += gone;
  }
  for (let i = 0; i < deltas.length; i++) knots[i + 1][1] = knots[i][1] + deltas[i];
  if (stats) {
    stats.before += jogs.length;
    stats.after += jogs.length - removed;
  }

  // where along each run the jog sits
  const jogAt = (k: number) => 0.25 + 0.5 * knotHash(k, from);
  const verts: Pt[] = [];
  for (let k = 0; k < knots.length - 1; k++) {
    const [ta, va] = knots[k];
    const [tb, vb] = knots[k + 1];
    const d = Math.abs(vb - va);
    const s = Math.max(ta, Math.min(tb - d, ta + (tb - ta) * jogAt(k) - d / 2));
    verts.push([ta, va]);
    if (d > 0) verts.push([s, va], [s + d, vb]);
  }
  verts.push(knots[knots.length - 1]);
  const at = (t: number) => {
    if (t <= verts[0][0]) return verts[0][1];
    let lo = 0;
    let hi = verts.length - 1;
    if (t >= verts[hi][0]) return verts[hi][1];
    while (hi - lo > 1) {
      const m = (lo + hi) >> 1;
      if (verts[m][0] <= t) lo = m;
      else hi = m;
    }
    const [t0, v0] = verts[lo];
    const [t1, v1] = verts[hi];
    return t1 === t0 ? v1 : v0 + ((v1 - v0) * (t - t0)) / (t1 - t0);
  };
  return { at, verts };
}

// Shift a polyline sideways by d with mitred corners, so street edges stay parallel through every bend.
function offsetPolyline(pts: Pt[], d: number): Pt[] {
  const p = pts.filter((q, i) => i === 0 || dist(q, pts[i - 1]) > 1e-6);
  const normal = (a: Pt, b: Pt): Pt => {
    const l = dist(a, b);
    return [-(b[1] - a[1]) / l, (b[0] - a[0]) / l];
  };
  const out: Pt[] = [];
  for (let i = 0; i < p.length; i++) {
    const n0 = i > 0 ? normal(p[i - 1], p[i]) : normal(p[0], p[1]);
    const n1 = i < p.length - 1 ? normal(p[i], p[i + 1]) : n0;
    const mx = n0[0] + n1[0];
    const my = n0[1] + n1[1];
    const ml = Math.hypot(mx, my) || 1;
    const scale = d / ((mx / ml) * n0[0] + (my / ml) * n0[1]);
    out.push([p[i][0] + (mx / ml) * scale, p[i][1] + (my / ml) * scale]);
  }
  return out;
}

// The visible area of the map at a given zoom, centered on the original 1200 x 560 frame.
function worldBox(zoom: number) {
  const width = 1200 * zoom;
  const height = 560 * zoom;
  return { left: 600 - width / 2, top: 280 - height / 2, width, height };
}

// Organic plat: curving streets with lots fanning off them, cross streets and cul-de-sacs, all from one recipe.
// precise = vector mode: no crooked lot lines, no gaps at junctions, every line ends exactly where it meets another.
// zoom > 1 keeps everything the same size and grows the map outward around the middle, like a camera pulling back.
// At zoom 1 every number below works out to the original layout.
function buildParcels(seed: number, lotScale: number, recipe: Recipe, variety: number, precise = false, zoom = 1, stats?: JogStats): Line[] {
  const rand = rng(seed);
  const pd = (n: number) => (precise ? 0 : n); // the small hand-drawn gaps and overruns at junctions
  const strokes = (pts: Pt[], keep: (p: Pt) => boolean, weight: number) => toStrokes(pts, keep, rand, weight, !precise);
  const district = field(rng(seed * 31 + 7));
  // > 0: bigger lots, < 0: tighter lots. Above `fullDepth`, parcels run the whole block with no rear line.
  const zone = (x: number, y: number) => Math.max(-1, Math.min(1, district(x, y))) * variety;
  const fullDepth = 0.45;
  const lines: Line[] = [];
  const W = 1200;
  const H = 560;
  const sw = 22; // street width
  const { left, top, width: worldW, height: worldH } = worldBox(zoom);
  const x0 = left - 160 * zoom;
  const x1 = left + worldW + 160 * zoom;
  const dx = precise ? 2 : 6;

  const bases: number[] = [];
  for (let y = top - 80 * zoom - rand() * 60; y < top + worldH + 160 * zoom; y += recipe.spacing * (1 + (rand() - 0.5) * recipe.spacingVar * 1.4)) bases.push(y);
  const common = wave(rand, 2);
  const smooth = bases.map(b => {
    const own = wave(rand, 2);
    const amp = recipe.wander * (0.6 + rand() * 0.8);
    return (x: number) => b + common(x / 1500) * recipe.curve + own(x / 900) * amp + recipe.bow * ((x - W / 2) / (worldW / 2)) ** 2;
  });
  // vector mode straightens every street into a circuit-trace route
  const routes = precise ? smooth.map(f => octilinear(f, x0, x1, 200, 10, stats)) : null;
  const streets = routes ? routes.map(r => r.at) : smooth;
  const slope = precise
    ? (f: (x: number) => number, x: number) => f(x + 0.5) - f(x - 0.5)
    : (f: (x: number) => number, x: number) => (f(x + 1) - f(x - 1)) / 2;
  // half the street's height measured straight up and down; wider on a 45° run
  const halfAcross = (f: (x: number) => number, x: number) => (precise ? (sw / 2) * Math.hypot(1, slope(f, x)) : sw / 2);

  // a wider map gets proportionally more cross streets, repeating the recipe's pattern
  const crossCount = recipe.cross.length ? Math.max(recipe.cross.length, Math.round(recipe.cross.length * zoom)) : 0;
  const smoothCross = Array.from({ length: crossCount }, (_, k) => {
    const c = recipe.cross[k % recipe.cross.length];
    const w = wave(rand, 2);
    const cx0 = left + ((k + 1) / (crossCount + 1)) * worldW + (rand() - 0.5) * 160 - c.lean * H * 0.5;
    return (y: number) => cx0 + w(y / 700) * c.amp + y * c.lean;
  });
  const y0 = top - 160 * zoom;
  const y1 = top + worldH + 160 * zoom;
  const crossRoutes = precise ? smoothCross.map(f => octilinear(f, y0, y1, 200, 10, stats)) : null;
  const crosses = crossRoutes ? crossRoutes.map(r => r.at) : smoothCross;
  const crossHalf = (cx: (y: number) => number, y: number) => (precise ? (sw / 2) * Math.hypot(1, cx(y + 0.5) - cx(y - 0.5)) : sw / 2);
  const inCross = (p: Pt, pad = 0) => crosses.some(cx => Math.abs(p[0] - cx(p[1])) < crossHalf(cx, p[1]) + pad);
  const body = precise ? sw / 2 : sw / 2 - 1;
  const onStreetBody = (p: Pt) => streets.some(f => Math.abs(p[1] - f(p[0])) < (precise ? halfAcross(f, p[0]) : body));

  // cul-de-sacs hang off a street into the block below it, in blocks deep enough to hold one
  const stem = 46;
  const bulb = 24;
  const cdsList: Cds[] = [];
  const deepBlocks = streets.map((f, i) => i).filter(i => i < streets.length - 1 && bases[i + 1] - bases[i] > 190 && bases[i] > top - 40 && bases[i] < top + worldH - 120);
  const cdsCount = Math.round(recipe.culDeSacs * zoom * zoom);
  for (let k = 0; k < cdsCount && deepBlocks.length; k++) {
    const i = deepBlocks[k % deepBlocks.length];
    const x = left + 140 + rand() * (worldW - 280);
    if (inCross([x, streets[i](x) + 60], 70) || cdsList.some(c => Math.abs(c.x - x) < 260 && Math.abs(c.street - i) < 1)) continue;
    const f = streets[i];
    const edge = (xx: number) => f(xx) + sw / 2;
    const C: Pt = [x, edge(x) + stem + bulb];
    const room = (bases[i + 1] - bases[i]) / 2 - stem - bulb + 10;
    const poly: Pt[] = [[x - 44 - rand() * 22, edge(x - 55)]];
    const corners = Math.max(3, Math.round(5 / lotScale));
    for (let c = 0; c < corners; c++) {
      const t = (c + 0.5 + (rand() - 0.5) * 0.5) / corners;
      const a = Math.PI * 1.08 - t * Math.PI * 1.16; // sweeps left, down, right
      const r = (bulb + Math.min(room + 40, 62 + rand() * 34)) * lotScale ** 0.4;
      poly.push([C[0] + Math.cos(a) * r * 1.35, C[1] + Math.sin(a) * r * 0.9]);
    }
    poly.push([x + 46 + rand() * 22, edge(x + 55)]);
    cdsList.push({ x, street: i, C, poly });
  }
  const inCds = (p: Pt) => cdsList.some(c => inPoly(c.poly, p));

  const rearJitter = wave(rand, 3);
  const rear = (i: number, side: 1 | -1) => {
    const j = i + side;
    // vector mode: the back-lot line is its own straight-run route between the two streets
    const mid = precise && j >= 0 && j < streets.length ? octilinear(x => (smooth[i](x) + smooth[j](x)) / 2, x0, x1, 200, 10, stats) : null;
    return (x: number) => {
      if (j < 0 || j >= streets.length) return streets[i](x) + side * (sw / 2 + 78 * lotScale);
      // the rear line jogs in small steps, as lots of different depths meet
      const step = Math.round(rearJitter(Math.floor(x / 46) * 0.37 + i) * 2.5 * (0.4 + variety));
      return (mid ? mid.at(x) : (streets[i](x) + streets[j](x)) / 2) + step * 5;
    };
  };

  // street edges
  streets.forEach((f, i) => {
    for (const side of [1, -1] as const) {
      let pts: Pt[] = [];
      if (routes) pts = resample(offsetPolyline(routes[i].verts, (side * sw) / 2), dx);
      else for (let x = x0; x <= x1; x += dx) {
        const m = slope(f, x);
        const n = Math.hypot(1, m);
        pts.push([x - (side * m * sw) / 2 / n, f(x) + (side * sw) / 2 / n]);
      }
      const keep = (p: Pt) => !inCross(p, pd(-2)) && !(side === 1 && cdsList.some(c => c.street === i && Math.abs(p[0] - c.x) < sw / 2));
      lines.push(...strokes(pts, keep, 1.2));
    }
  });

  // cross street edges
  crosses.forEach((cx, k) => {
    for (const side of [1, -1] as const) {
      let pts: Pt[] = [];
      if (crossRoutes) pts = resample(offsetPolyline(crossRoutes[k].verts.map(([y, x]) => [x, y] as Pt), (side * sw) / 2), dx);
      else for (let y = top - 160 * zoom; y <= top + worldH + 160 * zoom; y += dx) pts.push([cx(y) + (side * sw) / 2, y]);
      lines.push(...strokes(pts, p => !onStreetBody(p) && !crosses.some(o => o !== cx && Math.abs(p[0] - o(p[1])) < (precise ? crossHalf(o, p[1]) : body)), 1.2));
    }
  });

  // cul-de-sacs: stem, bulb, rear boundary and wedge lots
  for (const c of cdsList) {
    const stemTop = streets[c.street](c.x) + sw / 2;
    const bulbGap = Math.asin(Math.min(1, sw / 2 / bulb));
    // vector mode: the turnaround is an octagon with a flat top the stem runs into
    const octR = 30;
    const apothem = octR * Math.cos(Math.PI / 8);
    const stemEnd = precise ? c.C[1] - apothem : c.C[1] - bulb * Math.cos(bulbGap);
    for (const side of [1, -1]) {
      const sx = c.x + (side * sw) / 2;
      const top = precise ? streets[c.street](sx) + halfAcross(streets[c.street], sx) : stemTop;
      lines.push({ pts: [[sx, top], [sx, stemEnd]], weight: 1.2 });
    }
    const bulbPts: Pt[] = [];
    if (precise) {
      bulbPts.push([c.x + sw / 2, stemEnd]);
      for (let k = 0; k < 8; k++) {
        const a = -Math.PI / 2 + Math.PI / 8 + (k * Math.PI) / 4;
        bulbPts.push([c.C[0] + Math.cos(a) * octR, c.C[1] + Math.sin(a) * octR]);
      }
      bulbPts.push([c.x - sw / 2, stemEnd]);
    } else {
      for (let a = -Math.PI / 2 + bulbGap; a <= Math.PI * 1.5 - bulbGap; a += 0.12) bulbPts.push([c.C[0] + Math.cos(a) * bulb, c.C[1] + Math.sin(a) * bulb]);
    }
    lines.push({ pts: bulbPts, weight: 1.2 });
    // distance from the center to the octagon's outline in a given direction
    const octEdge = (a: number) => apothem / Math.cos(a - Math.round(a / (Math.PI / 4)) * (Math.PI / 4));
    for (let i = 0; i < c.poly.length - 1; i++) {
      lines.push(...strokes([c.poly[i], c.poly[i + 1]], p => !inCross(p, pd(2)) && !onStreetBody(p), 0.85));
    }
    for (let i = 1; i < c.poly.length - 1; i++) {
      const v = c.poly[i];
      if (inCross(v, pd(2))) continue;
      const a = Math.atan2(v[1] - c.C[1], v[0] - c.C[0]) + (rand() - 0.5) * pd(0.25);
      const r0 = precise ? octEdge(a) : bulb;
      lines.push({ pts: [[c.C[0] + Math.cos(a) * r0, c.C[1] + Math.sin(a) * r0], v], weight: 0.7 });
    }
  }

  // lots along each street side: lines run from the street edge out to the shared rear line
  streets.forEach((f, i) => {
    for (const side of [1, -1] as const) {
      const r = rear(i, side);
      const rearPts: Pt[] = [];
      for (let x = x0; x <= x1; x += dx) rearPts.push([x, r(x)]);
      const j = i + side;
      const hasNeighbor = j >= 0 && j < streets.length;
      const isFull = (p: Pt) => hasNeighbor && zone(p[0], p[1]) > fullDepth;
      lines.push(...strokes(rearPts, p => !inCross(p, pd(2)) && !inCds(p) && !isFull(p), 0.85));

      const [fMin, fMax] = recipe.frontage;
      let x = x0 + rand() * 30;
      let prev: { s: Pt; e: Pt } | null = null;
      while (x < x1) {
        const z = zone(x, f(x));
        const big = rand() < recipe.bigLots * (1 + Math.max(0, z) * 2);
        const normal = big ? fMax * 2 + rand() * fMax : fMin + rand() * (fMax - fMin);
        // districts: tight rowhouse strips where z is low, big parcels where it is high
        x += Math.max(7, normal * 2 ** (z * 2.2)) * lotScale;
        const m = slope(f, x);
        const n = Math.hypot(1, m);
        const nx = (-m / n) * side;
        const ny = (1 / n) * side;
        const tilt = (rand() - 0.5) * pd(recipe.tilt);
        const dxv = nx * Math.cos(tilt) - ny * Math.sin(tilt);
        const dyv = nx * Math.sin(tilt) + ny * Math.cos(tilt);
        const start: Pt = [x + (nx * sw) / 2, f(x) + (ny * sw) / 2];
        if (inCross(start, pd(3)) || inCds(start)) { prev = null; continue; }
        const full = isFull(start);
        let end: Pt = start;
        const at = (t: number): Pt => [start[0] + dxv * t, start[1] + dyv * t];
        const hits = (p: Pt) =>
          (full
            ? Math.abs(p[1] - streets[j](p[0])) < halfAcross(streets[j], p[0]) + pd(1) // big parcels run through to the next street
            : side * (p[1] - r(p[0])) >= 0) || inCross(p, pd(1)) || inCds(p);
        for (let t = 2; t < 420; t += 2) {
          const p = at(t);
          if (hits(p)) {
            if (precise) {
              // close in on the exact meeting point, so the line ends on the other line
              let lo = t - 2;
              let hi = t;
              for (let k = 0; k < 12; k++) { const mid = (lo + hi) / 2; if (hits(at(mid))) hi = mid; else lo = mid; }
              end = at(hi);
            } else end = p;
            break;
          }
          end = p;
        }
        if (dist(start, end) > 12) {
          lines.push({ pts: [start, end], weight: 0.7 });
          // now and then split a lot into front and back parcels
          if (prev && !full && z > -0.3 && rand() < 0.06 + 0.1 * variety) {
            const k = 0.4 + rand() * 0.3;
            const lerp = (a: Pt, b: Pt): Pt => [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
            lines.push({ pts: [lerp(prev.s, prev.e), lerp(start, end)], weight: 0.6 });
          }
          prev = { s: start, e: end };
        }
      }
    }
  });

  return lines;
}

export type Style = { pressure: number; overshoot: number; wobble: number; width: number };

function resample(pts: Pt[], step: number): Pt[] {
  const out: Pt[] = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const n = Math.max(1, Math.round(dist(a, b) / step));
    for (let k = 1; k <= n; k++) out.push([a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n]);
  }
  return out;
}

// One pencil stroke along a polyline: variable width and opacity, small wobble, ends that run past or stop short.
function strokeChunks(line: Line, style: Style, rand: () => number) {
  const raw = line.pts;
  const ext = (i: number, j: number) => {
    const d = dist(raw[i], raw[j]) || 1;
    const e = rand() < 0.15 ? -rand() * 2 : rand() * style.overshoot;
    return [raw[i][0] + ((raw[i][0] - raw[j][0]) / d) * e, raw[i][1] + ((raw[i][1] - raw[j][1]) / d) * e] as Pt;
  };
  const pts = resample([ext(0, 1), ...raw.slice(1, -1), ext(raw.length - 1, raw.length - 2)], 7);
  if (pts.length < 3) return [];

  const wob = wave(rand);
  const pres = wave(rand);
  const total = pts.reduce((s, p, i) => (i ? s + dist(pts[i - 1], p) : 0), 0);
  const base = style.width * line.weight;
  let along = 0;

  const ribs = pts.map((p, i) => {
    if (i) along += dist(pts[i - 1], p);
    const t = along / total;
    const q = pts[Math.min(pts.length - 1, i + 1)];
    const o = pts[Math.max(0, i - 1)];
    const tl = dist(o, q) || 1;
    const nx = -(q[1] - o[1]) / tl;
    const ny = (q[0] - o[0]) / tl;
    const off = wob(along / 260) * style.wobble;
    const taper = Math.min(1, t / 0.07, (1 - t) / 0.07);
    const pr = 1 - style.pressure * (0.5 + 0.5 * pres(along / 170));
    const w = base * Math.max(0.15, taper) * (0.45 + 0.55 * pr);
    const cx = p[0] + nx * off;
    const cy = p[1] + ny * off;
    return { l: [cx + (nx * w) / 2, cy + (ny * w) / 2] as Pt, r: [cx - (nx * w) / 2, cy - (ny * w) / 2] as Pt, pr };
  });

  const chunk = 4;
  const out: { d: string; o: number }[] = [];
  for (let i = 0; i < ribs.length - 1; i += chunk) {
    const slice = ribs.slice(i, Math.min(ribs.length - 1, i + chunk) + 1);
    const left = slice.map(q => q.l);
    const right = slice.map(q => q.r).reverse();
    const d = 'M' + [...left, ...right].map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join('L') + 'Z';
    const avg = slice.reduce((s, q) => s + q.pr, 0) / slice.length;
    out.push({ d, o: Math.round((0.35 + 0.65 * avg) * 1000) / 1000 });
  }
  return out;
}

export type Mode = 'hand' | 'vector';

// fill: stretch to cover the parent (for cards) instead of keeping the 1200 x 560 shape
export function ParcelArt({ seed, style, lotScale, recipe, variety, fade, id, mode, zoom, fill = false }: { seed: number; style: Style; lotScale: number; recipe: Recipe; variety: number; fade: number; id: string; mode: Mode; zoom: number; fill?: boolean }) {
  // Build and draw only in the browser: server math can round differently on large maps.
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  // line weight scales with zoom so lines keep the same thickness on screen as the camera pulls back
  const paths = useMemo(() => {
    if (!ready || mode === 'vector') return [];
    const rand = rng(seed * 7919 + 1);
    const s = { ...style, width: style.width * zoom };
    return buildParcels(seed, lotScale, recipe, variety, false, zoom).flatMap(l => strokeChunks(l, s, rand));
  }, [seed, style, lotScale, recipe, variety, mode, zoom, ready]);

  // vector: plain, even-width lines that meet exactly
  const { vectors, jogs } = useMemo(() => {
    const jogs: JogStats = { before: 0, after: 0 };
    if (!ready || mode !== 'vector') return { vectors: [], jogs };
    const byWidth = new Map<number, string>();
    for (const l of buildParcels(seed, lotScale, recipe, variety, true, zoom, jogs)) {
      const w = Math.round(style.width * zoom * l.weight * 0.6 * 100) / 100;
      const d = 'M' + l.pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join('L');
      byWidth.set(w, (byWidth.get(w) ?? '') + d);
    }
    const vectors = [...byWidth].map(([w, d]) => ({ w, d }));
    return { vectors, jogs };
  }, [seed, style.width, lotScale, recipe, variety, mode, zoom, ready]);

  const box = worldBox(zoom);
  if (!ready) return fill ? null : <div style={{ aspectRatio: '1200 / 560' }} />;
  return (
    <svg viewBox={`${box.left} ${box.top} ${box.width} ${box.height}`} className={fill ? "absolute inset-0 h-full w-full" : "w-full h-auto block"} preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-jogs-before={jogs.before} data-jogs-after={jogs.after}>
      <defs>
        <radialGradient id={`${id}-fade`} cx="50%" cy="58%" r="62%">
          {/* fade 0 = drawing runs to every edge, 1 = soft vignette that clears the edges */}
          <stop offset={`${100 - fade * 65}%`} stopColor="#fff" stopOpacity="1" />
          <stop offset="100%" stopColor="#fff" stopOpacity={1 - fade} />
        </radialGradient>
        <mask id={`${id}-mask`}>
          <rect x={box.left} y={box.top} width={box.width} height={box.height} fill={`url(#${id}-fade)`} />
        </mask>
      </defs>
      <g mask={`url(#${id}-mask)`} transform={`rotate(${recipe.rotate} 600 280)`} fill="currentColor">
        {paths.map((p, i) => <path key={i} d={p.d} opacity={p.o} />)}
        {vectors.map(v => <path key={v.w} d={v.d} fill="none" stroke="currentColor" strokeWidth={v.w} strokeLinejoin="miter" strokeLinecap="butt" />)}
      </g>
    </svg>
  );
}

/** One saved look for a card's texture. Tune it on the local /styles page, then paste the copied settings here. */
export type ParcelTextureSettings = {
  mode: Mode;
  /** Name of one of the `recipes` above, e.g. "Gentle suburb" */
  variation: string;
  /** Which neighborhood of that variation; any whole number */
  seed: number;
  lineWeight: number;
  zoom: number;
  lotSize: number;
  lotVariety: number;
  edgeFade: number;
  /** Opacity of the whole texture, 0–1 */
  strength: number;
  /** Hand-drawn only */
  pressure: number;
  overshoot: number;
  wobble: number;
};

export const defaultParcelTexture: ParcelTextureSettings = {
  mode: "vector",
  variation: "Gentle suburb",
  seed: 4,
  lineWeight: 1.6,
  zoom: 2,
  lotSize: 1,
  lotVariety: 0.7,
  edgeFade: 1,
  strength: 0.35,
  pressure: 0.7,
  overshoot: 7,
  wobble: 1.25,
};

interface ParcelTextureProps {
  settings: ParcelTextureSettings;
  /** Sets the line color through currentColor, e.g. "text-zinc-500" on light or "text-blue-400" on dark */
  className?: string;
}

/** Fills its nearest positioned parent with the texture, behind the parent's content. */
export function ParcelTexture({ settings, className = "text-zinc-500" }: ParcelTextureProps) {
  const id = useId().replace(/:/g, "");
  const recipe = recipes.find(r => r.name === settings.variation) ?? recipes[0];
  const style = useMemo<Style>(
    () => ({ pressure: settings.pressure, overshoot: settings.overshoot, wobble: settings.wobble, width: settings.lineWeight }),
    [settings.pressure, settings.overshoot, settings.wobble, settings.lineWeight],
  );
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} style={{ opacity: settings.strength }}>
      <ParcelArt
        fill
        id={`pt${id}`}
        seed={settings.seed}
        style={style}
        lotScale={settings.lotSize}
        recipe={recipe}
        variety={settings.lotVariety}
        fade={settings.edgeFade}
        mode={settings.mode}
        zoom={settings.zoom}
      />
    </div>
  );
}
